'use client'

import displayError from '@/api/errors/errorResponse'
import { putUserMe } from '@/api/services/auth/authService'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/forms/form/Form'
import { Input } from '@/components/forms/inputs/text/Input'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { Button } from '@/components/ui/common/Button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { displayToast } from '@/components/ui/toast/displayToast'
import useAuthStore from '@/store/userStore'
import { zodResolver } from '@hookform/resolvers/zod'
import { Separator } from '@radix-ui/react-select'
import { Calendar, Mail, MapPin, User, UserRound } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import DashboardLayout from '../dashboard/DashboardLayout'

const formSchema = z.object({
  firstName: z.string().min(2, 'Imię musi mieć minimum 2 znaki'),
  lastName: z.string().min(2, 'Nazwisko musi mieć minimum 2 znaki'),
  birthDate: z.string().min(1, 'Data urodzenia jest wymagana'),
  gender: z.enum(['MALE', 'FEMALE'])
})

type FormData = z.infer<typeof formSchema>

export default function UserProfileSettingsPage() {
  const [isEditing, setIsEditing] = useState(false)
  const { user } = useAuthStore()

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      birthDate: user?.birthDate,
      gender: user?.gender
    }
  })

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleSave = async (data: FormData) => {
    setIsEditing(false)
    const resp = await putUserMe({ ...data })
    if (!resp.success) {
      displayError(resp)
      return
    }
    displayToast('success', 'Sukces', 'Pomyślnie zmienione dane')
  }

  const handleCancel = () => {
    setIsEditing(false)
    form.reset()
  }

  const calculateAge = (birthDate: string) => {
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--
    }

    return age
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-4xl">
          <Card className="rounded-none border border-gray-200 shadow-sm">
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900">
                Profil użytkownika
              </CardTitle>
              <CardDescription className="text-gray-600">
                Zarządzaj swoimi danymi osobowymi i ustawieniami konta
              </CardDescription>
            </CardHeader>

            <Separator />

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSave)}>
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <div className="flex flex-col gap-6 md:flex-row">
                      {/* Avatar */}
                      <div className="flex flex-col items-center space-y-4">
                        <div className="overflow-hidden rounded-full border-2 border-[#2bcaca]/20 bg-gray-100 p-2">
                          <User className="h-32 w-32 text-gray-600" />
                        </div>
                      </div>

                      {/* Personal Info */}
                      <div className="flex-1 space-y-6">
                        {!isEditing ? (
                          <>
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                              <div className="space-y-4">
                                <div className="flex items-center space-x-3 bg-gray-50 p-3">
                                  <User className="h-5 w-5 text-[#2bcaca]" />
                                  <div>
                                    <p className="text-sm text-gray-500">
                                      Imię i nazwisko
                                    </p>
                                    <p className="font-medium text-gray-900">
                                      {form.getValues('firstName')}{' '}
                                      {form.getValues('lastName')}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center space-x-3 bg-gray-50 p-3">
                                  <Calendar className="h-5 w-5 text-[#2bcaca]" />
                                  <div>
                                    <p className="text-sm text-gray-500">Wiek</p>
                                    <p className="font-medium text-gray-900">
                                      {calculateAge(form.getValues('birthDate'))} lat
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center space-x-3 bg-gray-50 p-3">
                                  <MapPin className="h-5 w-5 text-[#2bcaca]" />
                                  <div>
                                    <p className="text-sm text-gray-500">Miejscowość</p>
                                    <p className="font-medium text-gray-900">Białystok</p>
                                  </div>
                                </div>
                              </div>

                              <div className="space-y-4">
                                <div className="flex items-center space-x-3 bg-gray-50 p-3">
                                  <UserRound className="h-5 w-5 text-[#2bcaca]" />
                                  <div>
                                    <p className="text-sm text-gray-500">Płeć</p>
                                    <p className="font-medium text-gray-900">
                                      {form.getValues('gender') === 'MALE'
                                        ? 'Mężczyzna'
                                        : 'Kobieta'}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center space-x-3 bg-gray-50 p-3">
                                  <Mail className="h-5 w-5 text-[#2bcaca]" />
                                  <div>
                                    <p className="text-sm text-gray-500">Email</p>
                                    <p className="font-medium text-gray-900">
                                      {user?.email}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="grid grid-cols-1 gap-4 pb-4 md:grid-cols-2">
                            <div className="space-y-4">
                              <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-gray-700">Imię</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        className="border-gray-200 focus:border-[#2bcaca] focus:ring-[#2bcaca]"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-gray-700">
                                      Nazwisko
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        className="border-gray-200 focus:border-[#2bcaca] focus:ring-[#2bcaca]"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="birthDate"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-gray-700">
                                      Data urodzenia
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        type="date"
                                        {...field}
                                        className="border-gray-200 focus:border-[#2bcaca] focus:ring-[#2bcaca]"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormItem>
                                <FormLabel className="text-gray-700">
                                  Miejscowość
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    value="Białystok"
                                    disabled
                                    className="border-gray-200 bg-gray-50"
                                  />
                                </FormControl>
                              </FormItem>
                            </div>

                            <div className="space-y-4">
                              <FormField
                                control={form.control}
                                name="gender"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-gray-700">Płeć</FormLabel>
                                    <Select
                                      value={field.value}
                                      onValueChange={field.onChange}>
                                      <FormControl>
                                        <SelectTrigger className="border-gray-200 focus:border-[#2bcaca] focus:ring-[#2bcaca]">
                                          <SelectValue placeholder="Wybierz płeć" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent className="border-none bg-white">
                                        <SelectItem value="FEMALE">Kobieta</SelectItem>
                                        <SelectItem value="MALE">Mężczyzna</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormItem>
                                <FormLabel className="text-gray-700">Email</FormLabel>
                                <FormControl>
                                  <Input
                                    value={user?.email}
                                    disabled
                                    className="border-gray-200 bg-gray-50"
                                  />
                                </FormControl>
                              </FormItem>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="flex justify-between border-t border-gray-200 pt-6">
                  {!isEditing ? (
                    <Button
                      onClick={handleEdit}
                      className="bg-[#2bcaca] text-white hover:bg-[#229999]">
                      Edytuj profil
                    </Button>
                  ) : (
                    <div className="flex space-x-2">
                      <Button
                        type="submit"
                        className="bg-[#2bcaca] text-white hover:bg-[#229999]">
                        Zapisz zmiany
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="border-[#2bcaca] text-[#2bcaca] hover:bg-[#2bcaca]/10">
                        Anuluj
                      </Button>
                    </div>
                  )}
                </CardFooter>
              </form>
            </Form>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  )
}
